import { components } from "@certn/api/generated-schemas/client-portal";

/**
 * Checks if a check type matches the international criminal record pattern.
 * International criminal checks follow the format: XX_CRIMINAL_RECORD_N
 * where XX is a 2-letter country code and N is a digit.
 *
 * @param checkType - The type string to check
 * @returns true if the check type is an international criminal record check
 */
export function isInternationalCriminalCheck(checkType: string): boolean {
  return /^[A-Z]{2}_CRIMINAL_RECORD_\d+$/.test(checkType);
}

/**
 * Gets the display name for a check based on the context.
 * For international criminal checks, uses coverage if available, otherwise falls back to name.
 * For other checks, uses the name.
 *
 * @param check - The check object
 * @param context - The context in which the display name is being used
 * @returns The display name for the check
 */
export function getCheckDisplayNameFromCheck(
  check: components["schemas"]["CheckListChild"],
  context: "FULL_LIST" | "DROPDOWN"
): string | undefined {
  const checkType = check.type || "";

  // Determine the original label based on check type
  const isIntlCheck = isInternationalCriminalCheck(checkType);
  const originalLabel = isIntlCheck ? check.coverage || check.name : check.name;

  return getCheckDisplayNameFromType(checkType, originalLabel || "", context);
}

/**
 * Gets the display name from a check type string and original label.
 * Uses the same logic as getCheckDisplayName but works with check type strings.
 *
 * @param checkType - The check type string
 * @param originalLabel - The original label (fallback)
 * @param context - The context in which the display name is being used
 * @returns The display name for the check type
 */
export function getCheckDisplayNameFromType(
  checkType: string,
  originalLabel: string,
  context: "FULL_LIST" | "DROPDOWN"
): string {
  const hasAsterisk = originalLabel.includes("*");
  const suffix = hasAsterisk ? "*" : "";

  // Special cases for Australian NCCHC checks
  if (checkType === "VOLUNTEER_AUSTRALIAN_NCCHC_1") {
    return context === "FULL_LIST"
      ? `Australia Nationally Coordinated Criminal History Check (NCCHC) – Volunteer${suffix}`
      : `Australia (NCCHC) – Volunteer${suffix}`;
  }

  if (checkType === "STANDARD_AUSTRALIAN_NCCHC_1") {
    return context === "FULL_LIST"
      ? `Australia Nationally Coordinated Criminal History Check (NCCHC) – Standard${suffix}`
      : `Australia (NCCHC) – Standard${suffix}`;
  }

  // For other cases, return the original label
  return `${originalLabel}${suffix}`;
}
